import { formatDistanceToNow } from 'date-fns';
import type { JobSummary } from '../lib/api';
import { dateFnsLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import { Badge, jobStateTone } from './Pill';

interface Props {
  jobs: JobSummary[];
  onSelect?: (job: JobSummary) => void;
  queueName?: string;
  showQueue?: boolean;
  /** Show shop ID column when job data includes it. */
  showShop?: boolean;
}

/** The queue a cross-queue listing tagged the job with, when it did. */
function queueNameOf(job: JobSummary): string | undefined {
  return 'queueName' in job && typeof job.queueName === 'string'
    ? job.queueName
    : undefined;
}

export function JobsTable({
  jobs,
  onSelect,
  showQueue,
  showShop = true,
}: Props) {
  const { t, locale } = useLocale();

  function age(job: JobSummary): string {
    return job.timestamp
      ? formatDistanceToNow(new Date(job.timestamp), {
          addSuffix: true,
          // Without this the column reads "3 minutes ago" on a German page — the
          // half of a translation nobody notices, because no key is missing.
          locale: dateFnsLocale(locale),
        })
      : '—';
  }

  if (jobs.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 text-sm">
        {t('jobs.empty')}
      </div>
    );
  }

  // Below `sm` the table is one column: the job, with its state, age and error underneath. Seven
  // columns on a phone meant the job name filled the screen and the error — the reason anyone
  // opens this list — sat a sideways scroll away. `table-fixed` gives that one column the row's
  // width, which is what lets its long names truncate instead of pushing the table wider.
  return (
    <div className="overflow-x-auto">
      <table className="data-table table-fixed sm:table-auto sm:min-w-[500px]">
        <thead>
          <tr>
            <th className="pb-3 pr-4">{t('jobs.columnJob')}</th>
            {showQueue && (
              <th className="hidden sm:table-cell pb-3 pr-4">
                {t('jobs.columnQueue')}
              </th>
            )}
            {showShop && (
              <th className="hidden sm:table-cell pb-3 pr-4">
                {t('jobs.columnShop')}
              </th>
            )}
            <th className="hidden sm:table-cell pb-3 pr-4">
              {t('jobs.columnState')}
            </th>
            <th className="hidden sm:table-cell pb-3 pr-4">
              {t('jobs.columnAttempts')}
            </th>
            <th className="hidden sm:table-cell pb-3 pr-4">
              {t('jobs.columnTime')}
            </th>
            <th className="hidden sm:table-cell pb-3">
              {t('jobs.columnError')}
            </th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr
              key={job.id}
              onClick={() => onSelect?.(job)}
              className={`transition-colors ${onSelect ? 'cursor-pointer hover:bg-gray-50' : ''}`}
            >
              <td className="py-3 pr-4">
                <p className="font-mono text-xs text-gray-500 mb-0.5">
                  #{job.id}
                </p>
                <p className="font-medium text-gray-900 break-all sm:break-normal">
                  {job.name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 truncate sm:max-w-xs">
                  {job.dataPreview}
                </p>
                <div className="sm:hidden mt-1.5 space-y-1">
                  {(showQueue || (showShop && job.shopId)) && (
                    <p className="font-mono text-xs text-gray-500 truncate">
                      {showQueue && (queueNameOf(job) ?? '—')}
                      {showQueue && showShop && job.shopId ? ' · ' : ''}
                      {showShop && job.shopId}
                    </p>
                  )}
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                    <Badge tone={jobStateTone(job.state)}>{job.state}</Badge>
                    <span className="tabular-nums">
                      {job.attemptsMade}/{job.maxAttempts}
                    </span>
                    <span>{age(job)}</span>
                  </p>
                  {job.failedReason && (
                    <p className="text-xs text-red-600 line-clamp-2 break-words">
                      {job.failedReason}
                    </p>
                  )}
                </div>
              </td>
              {showQueue && (
                <td className="hidden sm:table-cell py-3 pr-4">
                  <span className="text-gray-700 font-mono text-xs">
                    {queueNameOf(job) ?? '—'}
                  </span>
                </td>
              )}
              {showShop && (
                <td className="hidden sm:table-cell py-3 pr-4">
                  <span className="text-gray-500 font-mono text-xs truncate max-w-24 block">
                    {job.shopId ?? '—'}
                  </span>
                </td>
              )}
              <td className="hidden sm:table-cell py-3 pr-4">
                <Badge tone={jobStateTone(job.state)}>{job.state}</Badge>
              </td>
              <td className="hidden sm:table-cell py-3 pr-4 text-gray-600 tabular-nums">
                {job.attemptsMade}/{job.maxAttempts}
              </td>
              <td className="hidden sm:table-cell py-3 pr-4 text-gray-500 text-xs whitespace-nowrap">
                {age(job)}
              </td>
              <td className="hidden sm:table-cell py-3 text-xs text-red-600 max-w-xs truncate">
                {job.failedReason ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
